import { ReactNode, useMemo, useState } from 'react';
import { TaxiTable } from '@/components/TaxiTable';
import GroupsIcon from '@mui/icons-material/Groups';
import WalletIcon from '@mui/icons-material/Wallet';
import CastIcon from '@mui/icons-material/Cast';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import { Trans, useTranslation } from 'react-i18next';
import {
  useGetBudgetQuery,
  useGetScreensSummaryQuery,
} from '@/models/company-screens';
import { CardStats } from '@/features/card-stats';
import { useProfileQuery } from '@/models/user';
import { IconButton, Typography } from '@mui/material';
import {
  useGetPaymentsPartnerLastMonthQuery,
  useGetPaymentsPartnerTotalSumQuery,
} from '@/models/payments';
import { Card } from '@/features/card';
import { Link } from 'react-router';
import { InfoModal } from '@/commons/info-modal';
import InfoIcon from '@mui/icons-material/Info';
import {
  Card1TooltipContent,
  Card2TooltipContent,
  PointsCardTooltipContent,
  SummaryCardTooltipContent,
} from './tooltipContents';

export const PartnerHome = () => {
  const { data } = useProfileQuery();
  const companyName = data?.companyName;
  const { data: summaryData } = useGetScreensSummaryQuery();
  const { data: totalSumData } = useGetPaymentsPartnerTotalSumQuery();
  const { data: budgetData } = useGetBudgetQuery();
  const { data: lastMonthPaymentData } = useGetPaymentsPartnerLastMonthQuery();
  const { t } = useTranslation();
  const [infoModalIsOpen, setInfoIsModalOpen] = useState(false);
  const [tooltip, setTooltip] = useState<string | ReactNode>('');

  const totalMtdPoints = summaryData?.total_mtd_points || 0;
  const totalMtdPointsAll = summaryData?.total_mtd_points_all || 0;
  const totalScreensOnline = summaryData?.total_screens_online || 0;
  const totalMtdPassengers = summaryData?.total_mtd_passengers || 0;
  const currentMonthBudget = budgetData?.currentMonth?.budget || '0.00';
  const previousMonthBudget = budgetData?.previousMonth?.budget || '0.00';
  const totalEarnedWithUs = totalSumData?.amount || 0;
  const totalBonusReceivedFromUs = totalSumData?.bonus || 0;

  const currentMonthBalance = useMemo(() => {
    if (!totalMtdPointsAll || totalMtdPointsAll === 0) return '0.00';
    const result =
      (totalMtdPoints / totalMtdPointsAll) * parseFloat(currentMonthBudget);
    if (!isFinite(result) || isNaN(result)) return '0.00';
    return Math.ceil(result).toFixed(2);
  }, [totalMtdPoints, totalMtdPointsAll, currentMonthBudget]);

  const handleTooltipClick = (content: string | ReactNode) => {
    setInfoIsModalOpen(true);
    setTooltip(content);
  };

  return (
    <div className="flex flex-col w-full px-3 sm:px-4 md:px-6">
      {companyName && (
        <div className="mb-6">
          <Typography variant="h5" fontWeight={500}>
            {t('label.welcome')} {companyName}
          </Typography>
        </div>
      )}

      {/* Responsive stats section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1 */}
        <CardStats
          classNames="bg-primary-50"
          boxShadowColor="#f3ced7"
          currentLabel={<Trans i18nKey="partner.total_current_month_budget" />}
          currentValue={
            <div>
              <span className="text-2xl sm:text-3xl mr-1">
                {parseFloat(currentMonthBudget).toFixed(2)}
              </span>
              <Trans i18nKey="common.label.pln" />
            </div>
          }
          previousLabel={
            <Trans i18nKey="partner.total_previous_month_budget" />
          }
          previousValue={
            <div>
              <span className="text-sm mr-1 font-bold">
                {parseFloat(previousMonthBudget).toFixed(2)}
              </span>
              <span className="text-xs font-bold">
                <Trans i18nKey="common.label.pln" />
              </span>
            </div>
          }
          icon={
            <div className="bg-primary-500 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full">
              <WalletIcon style={{ fontSize: 22, color: '#ffffff' }} />
            </div>
          }
          hasTooltip={true}
          toolTipClicked={() => handleTooltipClick(<Card1TooltipContent />)}
        />

        {/* 2 */}
        <CardStats
          currentLabel={<Trans i18nKey="partner.current_month_balance" />}
          currentValue={
            <div>
              <span className="text-2xl sm:text-3xl mr-1">
                {currentMonthBalance}
              </span>
              <Trans i18nKey="common.label.pln" />
            </div>
          }
          previousLabel={<Trans i18nKey="partner.previous_month_balance" />}
          previousValue={
            <Link to="/partner/payments" className="hover:underline">
              <span className="text-sm font-bold mr-1">
                {lastMonthPaymentData?.amount || '0.00'}
              </span>
              <span className="text-xs">
                <Trans i18nKey="common.label.pln" />
              </span>
            </Link>
          }
          icon={
            <div className="bg-gray-50 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full">
              <WalletIcon style={{ fontSize: 22, color: '#fc0841' }} />
            </div>
          }
          hasTooltip={true}
          toolTipClicked={() => handleTooltipClick(<Card2TooltipContent />)}
        />

        {/* 3 */}
        <CardStats
          currentLabel={<Trans i18nKey="label.total_views" />}
          currentValue={
            <span className="text-2xl sm:text-4xl mr-1">
              {totalMtdPassengers}
            </span>
          }
          icon={
            <div className="bg-gray-50 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full">
              <GroupsIcon style={{ fontSize: 22, color: '#fc0841' }} />
            </div>
          }
        />

        {/* 4 */}
        <CardStats
          currentLabel={<Trans i18nKey="label.total_screens_online" />}
          currentValue={
            <span className="text-2xl sm:text-4xl mr-1">
              {totalScreensOnline}
            </span>
          }
          icon={
            <div className="bg-gray-50 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full">
              <CastIcon style={{ fontSize: 22, color: '#fc0841' }} />
            </div>
          }
        />

        {/* 5 */}
        <CardStats
          currentLabel={<Trans i18nKey="label.total_points_collected" />}
          currentValue={
            <span className="text-2xl sm:text-4xl mr-1">{totalMtdPoints}</span>
          }
          icon={
            <div className="bg-gray-50 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full">
              <DataUsageIcon style={{ fontSize: 22, color: '#fc0841' }} />
            </div>
          }
          classNames="hover:cursor-pointer hover:bg-slate-50"
          hasTooltip={true}
          toolTipClicked={() =>
            handleTooltipClick(<PointsCardTooltipContent />)
          }
        />

        {/* Earnings Card */}
        <Card
          className="bg-primary-600 text-white flex flex-col items-center justify-center p-6 sm:p-8 col-span-1 sm:col-span-2 md:row-start-2 md:col-start-2 md:col-span-1 md:row-span-2 lg:col-start-4 lg:row-start-1 lg:row-span-2 order-first sm:order-none relative"
          boxShadowColor="#dc345e"
        >
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="text-white text-lg lg:text-base sm:text-xl">
              <Trans i18nKey="partner.total_you_made" />
            </span>
            <div>
              <span className="text-3xl lg:text-2xl mr-1">
                {(totalEarnedWithUs + totalBonusReceivedFromUs).toFixed(2)}
              </span>
              <span className="text-3xl lg:text-2xl mr-1">
                <Trans i18nKey="common.label.pln" />
              </span>
            </div>

            <div className="flex flex-col 2xl:flex-row items-center justify-center gap-4 text-primary-100">
              <div className="flex flex-col items-center">
                <span className="text-xs">
                  <Trans i18nKey="partner.total_earned" />
                </span>
                <div className="flex items-end">
                  <span className="text-2xl mr-1">
                    {totalEarnedWithUs.toFixed(2)}
                  </span>
                  <span className="text-lg sm:text-lg">
                    <Trans i18nKey="common.label.pln" />
                  </span>
                </div>
              </div>
              <span className="text-xl sm:text-2xl">+</span>
              <div className="flex flex-col items-center">
                <span className="text-xs">
                  <Trans i18nKey="partner.total_bonus_received" />
                </span>
                <div className="flex items-end">
                  <span className="text-2xl sm:text-2xl mr-1">
                    {totalBonusReceivedFromUs.toFixed(2)}
                  </span>
                  <span className="text-lg sm:text-lg">
                    <Trans i18nKey="common.label.pln" />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 right-1">
            <IconButton
              onClick={() => handleTooltipClick(<SummaryCardTooltipContent />)}
            >
              <InfoIcon sx={{ fontSize: '14px' }} />
            </IconButton>
          </div>
        </Card>
      </div>

      <InfoModal
        title={t('basic_info_section.title')}
        open={infoModalIsOpen}
        onClose={() => setInfoIsModalOpen(false)}
      >
        {tooltip}
      </InfoModal>

      {/* Taxi Table */}
      <div className="mt-7 mb-5">
        <TaxiTable />
      </div>
    </div>
  );
};
